import { useContext, useMemo } from "react";
import { BillingInfo } from "lens-platform-sdk";
import { misc } from "@k8slens/lds-icons";
import { LoadingIndicator } from "@k8slens/lds";

import { formatCreditCardNumber, formatCreditCardType } from "src/utils/credit-card";
import { useCanUpdateBillingInfo } from "src/hooks/useCanUpdateBillingInfo";

import { BusinessContext } from "src/components/Business/Base";
import PageAction from "src/components/PageAction/PageAction";
import TrackedRouterButton from "src/components/Button/TrackedRouterButton";
import TrackedAnchorButton from "src/components/TrackedAnchor/TrackedAnchorButton";

import styles from "./BillingDetails.module.css";

const { CreditCardIcon, ReceiptIcon } = misc;

const formatValidity = (paymentMethod: NonNullable<BillingInfo["paymentMethod"]>) =>
  paymentMethod.expYear && paymentMethod.expMonth
    ? `${paymentMethod.expMonth}/${paymentMethod.expYear?.toString().slice(2, 4)}`
    : "n/a";

export interface BillingProps {
  billingInfo?: BillingInfo | null;
  updateBillingInfoUrl: string | undefined;
  billingInfoUrl: string | undefined;
  loading?: boolean;
  loadingBilling?: boolean;
}

export const getAddressString = (address?: BillingInfo["address"]) => {
  if (address) {
    return [address.street1, address.street2, address.city, address.region, address.postalCode, address.country]
      .filter((x) => x)
      .join(", ");
  }

  return "";
};

export const Billing = ({ billingInfo, updateBillingInfoUrl, loadingBilling }: BillingProps) => {
  const { businessId, business } = useContext(BusinessContext);
  const { canUpdateBillingInfo, loading: loadingCanUpdateBillingInfo } = useCanUpdateBillingInfo(businessId);

  const isPaperInvoicingCustomer = Boolean(business?.external);
  const isCreditCardCustomer = billingInfo?.type === "credit_card" && !isPaperInvoicingCustomer;
  const isPaypalCustomer = billingInfo?.type === "paypal" && !isPaperInvoicingCustomer;
  const isInvoicingCustomer = billingInfo?.type === "invoice" && !isPaperInvoicingCustomer && !isPaypalCustomer;
  const noBillingInfo = !loadingBilling && !isPaperInvoicingCustomer && !billingInfo?.type;
  const addressString = getAddressString(billingInfo?.address);
  const loadingData = !(
    noBillingInfo ||
    isCreditCardCustomer ||
    isPaperInvoicingCustomer ||
    isInvoicingCustomer ||
    isPaypalCustomer
  );

  const updatePaymentMethodDisabledTooltip = useMemo(() => {
    if (isPaperInvoicingCustomer) {
      return "Cannot change payment method for this account. Please contact your sales representative to modify the payment method.";
    }

    return undefined;
  }, [isPaperInvoicingCustomer]);

  const updateButton = useMemo(() => {
    if (loadingData || (isPaypalCustomer && !updateBillingInfoUrl)) {
      return null;
    }

    if (isPaypalCustomer && updateBillingInfoUrl) {
      return (
        <PageAction
          label="Update billing information"
          buttonComponent={TrackedAnchorButton}
          buttonProps={{
            href: updateBillingInfoUrl,
            discreet: true,
            textTransform: "capitalize",
            target: "_blank",
            rel: "noopener noreferrer",
          }}
        />
      );
    } else {
      return (
        <PageAction
          label="Update billing information"
          buttonComponent={TrackedRouterButton}
          title={updatePaymentMethodDisabledTooltip}
          buttonProps={{
            disabled:
              loadingCanUpdateBillingInfo || !canUpdateBillingInfo || Boolean(updatePaymentMethodDisabledTooltip),
            to: !businessId ? "/" : `/business/${businessId}/billing-details/update`,
            discreet: true,
            textTransform: "capitalize",
          }}
        />
      );
    }
  }, [
    businessId,
    canUpdateBillingInfo,
    isPaypalCustomer,
    loadingCanUpdateBillingInfo,
    loadingData,
    updateBillingInfoUrl,
    updatePaymentMethodDisabledTooltip,
  ]);

  return (
    <div>
      {!loadingData && noBillingInfo ? <div className={styles.noPaymentMethod}>No payment method</div> : null}
      {isCreditCardCustomer ? (
        <div className={styles.creditCard}>
          <CreditCardIcon className={styles.paymentType} size="xxl" />
          {billingInfo.paymentMethod ? (
            <div className={styles.creditCardDetails}>
              <span aria-label={formatCreditCardNumber(billingInfo.paymentMethod, "text")}>
                {formatCreditCardType(billingInfo.paymentMethod.cardType)}{" "}
                {formatCreditCardNumber(billingInfo.paymentMethod, "short", "*")}
              </span>
              <span aria-hidden>Valid until: {formatValidity(billingInfo.paymentMethod)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {isPaperInvoicingCustomer ? (
        <div className={styles.paperInvocing}>
          <ReceiptIcon className={styles.paymentType} size="xxl" />{" "}
          <div className={styles.paperInvocingDetails}>
            <strong>Paper Invoicing</strong>
            {addressString ? <span className={styles.address}>{addressString}</span> : null}
          </div>
        </div>
      ) : null}
      {isPaypalCustomer ? (
        <div className={styles.paypal}>
          <ReceiptIcon className={styles.paymentType} size="xxl" />{" "}
          <div className={styles.paypalDetails}>
            <strong>PayPal</strong>
          </div>
        </div>
      ) : null}
      {isInvoicingCustomer ? (
        <div className={styles.paperInvocing}>
          <ReceiptIcon className={styles.paymentType} size="xxl" />{" "}
          <div className={styles.paperInvocingDetails}>
            <strong>Invoicing</strong>
            {addressString ? (
              <span className={styles.address}>
                {billingInfo?.company || business?.name} {addressString}
              </span>
            ) : (
              "Billing address not available. Please contact support to update your information."
            )}
          </div>
        </div>
      ) : null}
      {loadingData ? (
        <div className={styles.noPaymentMethod}>
          <LoadingIndicator size="sm" />
        </div>
      ) : null}
      {updateButton}
    </div>
  );
};
